import { isAssignableDoctor } from "@constants/doctor-access";

import { useAdminUsers } from "@hooks/queries/use-admin-users";
import { useIsAdmin } from "@hooks/queries/use-me";

export type DoctorOption = {
  /** The user id — what the assign endpoints take. */
  value: string;
  label: string;
  /** Email, shown as a secondary line and searchable. */
  hint: string;
  /** Email — the create endpoint resolves the doctor by email, not id. */
  email: string;
};

/**
 * The assignable-doctor list for the case pickers (MDRS-234).
 *
 * Three surfaces need this — Create case, Update case and the case detail Access
 * card — and each had its own `useAdminUsers → filter → map` chain. They had
 * drifted: Create keyed options on email with a single-line "Name (email)" label
 * while the other two keyed on id with a two-line label/hint, so the same picker
 * looked different depending on where you opened it.
 *
 * Admin-only: `enabled` keeps a non-admin from calling the admin users endpoint
 * at all, and the pickers themselves are hidden for non-admins anyway.
 */
export const useDoctorOptions = () => {
  const isAdmin = useIsAdmin();
  const { data: users = [], isLoading } = useAdminUsers({ enabled: isAdmin });

  const options: DoctorOption[] = users
    // `is_admin` is independent of `user_type` — an admin commonly has
    // `user_type === "internal"`, itself doctor-shaped, so it isn't enough on
    // its own to rule out an admin who ended up owning a case (MDRS-347).
    .filter((u) => isAssignableDoctor(u.user_type) && !u.is_admin)
    .map((u) => ({
      value: u.id,
      label: u.name,
      hint: u.email,
      email: u.email,
    }));

  return { options, isLoading, isAdmin };
};
